import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { SubjectFormData } from '../types';

interface SubjectDialogProps {
  isDark: boolean;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  formData: SubjectFormData;
  onFormDataChange: React.Dispatch<React.SetStateAction<SubjectFormData>>;
  onSubmit: () => Promise<void>;
  title: string;
  description: string;
  submitLabel: string;
  isEdit?: boolean;
}

export function SubjectDialog({
  isDark,
  isOpen,
  onOpenChange,
  formData,
  onFormDataChange,
  onSubmit,
  title,
  description,
  submitLabel,
  isEdit,
}: SubjectDialogProps) {
  const inputCls = cn(
    isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200',
  );
  const labelCls = cn(isDark ? 'text-slate-300' : 'text-slate-700');

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'max-w-2xl max-h-[90vh] overflow-y-auto',
          isDark ? 'bg-[#1E293B] border-slate-700' : 'bg-white',
        )}
      >
        <DialogHeader>
          <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#0F172A]')}>{title}</DialogTitle>
          <DialogDescription className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>
            {description}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={labelCls}>Slug *</Label>
              <Input
                value={formData.slug}
                onChange={(e) => onFormDataChange((prev) => ({ ...prev, slug: e.target.value }))}
                placeholder="matematika"
                disabled={isEdit}
                className={inputCls}
              />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Sort order</Label>
              <Input
                type="number"
                value={formData.sort_order ?? 0}
                onChange={(e) =>
                  onFormDataChange((prev) => ({
                    ...prev,
                    sort_order: Number.isNaN(Number(e.target.value)) ? 0 : Number(e.target.value),
                  }))
                }
                className={inputCls}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Name (uz, latin) *</Label>
            <Input
              value={formData.name}
              onChange={(e) => onFormDataChange((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Matematika"
              className={inputCls}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={labelCls}>Name (ru)</Label>
              <Input
                value={formData.name_ru || ''}
                onChange={(e) => onFormDataChange((prev) => ({ ...prev, name_ru: e.target.value }))}
                placeholder="Математика"
                className={inputCls}
              />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Name (kiril)</Label>
              <Input
                value={formData.name_kiril || ''}
                onChange={(e) => onFormDataChange((prev) => ({ ...prev, name_kiril: e.target.value }))}
                placeholder="Математика"
                className={inputCls}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Icon (URL or name)</Label>
            <Input
              value={formData.icon || ''}
              onChange={(e) => onFormDataChange((prev) => ({ ...prev, icon: e.target.value }))}
              placeholder="https://..."
              className={inputCls}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className={cn(isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-200')}
          >
            Cancel
          </Button>
          <Button onClick={onSubmit} className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90">
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
